import { useEffect, useState } from 'react';

import {
  ByTermKeys,
  MonthDay,
  OnceKeys
} from '@components/payment-collection/constants';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { PaymentCollectionType } from '@features/group-management/constants';
import {
  formatDueDatePlaceholder,
  getPaymentCollectionComponentType,
  getPaymentCollectionType
} from '@features/group-management/helper';
import { getDefaultTermsListForCreate } from '@features/group-management/helpers/customTerm';
import useGroupEndDate from '@features/group-management/hooks/useGroupEndDate';
import { useCheckGroupPaymentLinkSentMutation } from '@features/group-management/queries/GroupQuery';
import {
  FormDataType,
  MemberListType,
  PaymentCollectionArg,
  PaymentCollectionComponentType,
  ValidationError
} from '@types';
import { getLocalStorageItem } from '@utils/storage';

import GroupDueDateConsent from '../group-details-input/GroupDueDateConsent';
import GroupDueDateSelection from '../group-due-date-selection/GroupDueDateSelection';
import GroupEndDateSelection from '../group-end-date-selection/GroupEndDateSelection';
import GroupStartDateSelection from '../group-start-date-selection/GroupStartDateSelection';

interface GroupDateSettingsProps {
  formData: FormDataType;
  groupId: string;
  startDate: string;
  setStartDate: (value: string) => void;
  onInputChange: (fieldName: string, value: any) => void;
  error?: ValidationError;
  setError?: (value: ValidationError) => void;
  isEditGroup: boolean;
  memberList?: MemberListType[];
  groupDateClassName?: string;
  groupStartDateClassName?: string;
  groupEndDateClassName?: string;
  startDateAlertClassName?: string;
  byTermAdvancedDefaultLabel?: string;
  onPaymentLinkSentChange?: (isSent: boolean) => void;
}

const GroupDateSettings = ({
  formData,
  groupId,
  startDate,
  setStartDate,
  onInputChange,
  error,
  setError,
  isEditGroup,
  memberList,
  groupDateClassName = '',
  groupStartDateClassName = '',
  groupEndDateClassName = '',
  startDateAlertClassName = '',
  byTermAdvancedDefaultLabel,
  onPaymentLinkSentChange
}: GroupDateSettingsProps) => {
  const [isPaymentCollectionModalOpen, setIsPaymentCollectionModalOpen] =
    useState<boolean>(false);
  const [isStartDateModalOpen, setIsStartDateModalOpen] =
    useState<boolean>(false);
  const [paymentCollectionFrequency, setPaymentCollectionFrequency] =
    useState<string>(PaymentCollectionType.MONTHLY);
  const [showConsent, setShowConsent] = useState<boolean>(false);

  const [checkPaymentLinkSent] = useCheckGroupPaymentLinkSentMutation();

  const {
    isEndDateModalOpen,
    setIsEndDateModalOpen,
    endDateMinDate,
    endDateSelectedDate,
    onEndDateChange,
    onClearEndDate,
    formatDate
  } = useGroupEndDate({
    formData,
    onInputChange,
    hasCustomTermValidation: true,
    error,
    setError
  });

  const checkDueConsent = async (data: PaymentCollectionArg) => {
    const getIntervalValue = () => {
      if (data.type === PaymentCollectionType.WEEKLY)
        return PaymentCollectionType.WEEKLY;
      if (data.type === PaymentCollectionType.ONCE)
        return PaymentCollectionType.ONCE;
      return PaymentCollectionType.MONTHLY;
    };
    const res = await checkPaymentLinkSent({
      params: {
        organizationId: getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID),
        branchId: getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID),
        groupId: groupId || ''
      },
      body: {
        start_date: formatDate(startDate),
        interval_frequency: Number(data.interval),
        interval: getIntervalValue(),
        ...(getIntervalValue() === PaymentCollectionType.MONTHLY && {
          monthly_config: {
            day_option:
              // eslint-disable-next-line no-nested-ternary
              data.date === '1'
                ? MonthDay.FIRST
                : data.date === MonthDay.FIRST || data.date === MonthDay.LAST
                ? data.date
                : 'custom',
            ...(data.date !== MonthDay.FIRST &&
              data.date !== MonthDay.LAST &&
              data.date !== '1' && {
                custom_day: Number(data.date)
              }) // 2 to 28
          }
        }),
        ...(getIntervalValue() === PaymentCollectionType.WEEKLY && {
          weekly_config: {
            day_of_week: data.date
          }
        })
      }
    });
    if ('data' in res) {
      const isSent = res.data?.is_payment_link_sent || false;
      if (onPaymentLinkSentChange) {
        onPaymentLinkSentChange(isSent);
      } else {
        setShowConsent(isSent);
      }
    } else if (onPaymentLinkSentChange) {
      onPaymentLinkSentChange(false);
    } else {
      setShowConsent(false);
    }
  };

  const onStartDateChange = (date: any) => {
    const formattedStartDate = formatDate(date);
    setStartDate(formattedStartDate);
    onInputChange('startDate', formattedStartDate);
    setIsStartDateModalOpen(false);
  };

  const onDueDateChange = (data: PaymentCollectionArg) => {
    if (!isEditGroup) {
      onInputChange('intervalFrequency', data.interval);
      onInputChange('paymentCollectionType', data.type);
      memberList?.forEach((member) => {
        if (
          member.dueDate === formData.activationDate &&
          member.paymentCollectionType === formData.paymentCollectionType
        ) {
          if (
            data.date !== OnceKeys.IMMEDIATELY &&
            data.date !== ByTermKeys.ADVANCED
          )
            member.dueDate =
              data.type === 'once'
                ? formatDate(data.date)
                : (data.date as string);
          member.paymentCollectionType = data.type;
          member.interval = data.interval;
        }
      });
    }
    setPaymentCollectionFrequency(data.type);
    if (data.type === 'once' && data.date === OnceKeys.IMMEDIATELY) {
      onInputChange('isImmediatePayment', true);
      onInputChange('isCustomTerm', false);
    } else if (data.type === 'by_term' && data.date === ByTermKeys.ADVANCED) {
      onInputChange('isCustomTerm', true);
      onInputChange('isImmediatePayment', false);
    } else {
      onInputChange(
        'activationDate',
        data.type === 'once' ? formatDate(data.date) : data.date
      );
      onInputChange('isImmediatePayment', false);
      onInputChange('isCustomTerm', false);
    }
    if (isEditGroup && data.type !== PaymentCollectionType.ONCE)
      checkDueConsent(data);
    if (data.type === 'once') {
      onStartDateChange(new Date().toString());
      onClearEndDate();
    }
    if (
      data.type === 'by_term' &&
      data.date === ByTermKeys.ADVANCED &&
      !isEditGroup
    ) {
      onInputChange(
        'termsList',
        getDefaultTermsListForCreate(byTermAdvancedDefaultLabel)
      );
    }
    setIsPaymentCollectionModalOpen(false);
  };

  const isDueDateDisabled =
    isEditGroup &&
    (!!formData.isCustomTerm ||
      (paymentCollectionFrequency === PaymentCollectionType.ONCE &&
        (!!formData.isImmediatePayment ||
          (!!formData.isPaymentLinkSent &&
            !!formData.collectionStartedAt &&
            formData.collectionStartedAt !== 'null' &&
            new Date(formData.collectionStartedAt) < new Date()))));

  const isStartDateDisabled =
    paymentCollectionFrequency === PaymentCollectionType.ONCE ||
    (isEditGroup &&
      (!!formData.isCollectionStarted ||
        (!!formData.isCustomTerm &&
          !!formData.termsList &&
          formData.termsList.some((term) => term.isPaymentLinkSent)))) ||
    !!formData.isCustomTerm;

  const isEndDateDisabled =
    paymentCollectionFrequency === PaymentCollectionType.ONCE;

  const dueDateValue =
    formData.activationDate ||
    formData.isImmediatePayment ||
    formData.isCustomTerm
      ? (formatDueDatePlaceholder(
          isEditGroup
            ? getPaymentCollectionType(
                formData.paymentCollectionType,
                Number(formData.intervalFrequency)
              )
            : (formData.paymentCollectionType as PaymentCollectionType),
          Number(formData.intervalFrequency),
          formData.activationDate,
          formData.isImmediatePayment || formData.isCustomTerm,
          byTermAdvancedDefaultLabel
        ) as string)
      : formData.activationDate;

  const paymentCollectionComponentType = formData.activationDate
    ? (getPaymentCollectionComponentType(
        formData.paymentCollectionType,
        Number(formData.intervalFrequency)
      ) as PaymentCollectionComponentType)
    : PaymentCollectionComponentType.DEFAULT;

  useEffect(() => {
    if (formData.paymentCollectionType) {
      setPaymentCollectionFrequency(formData.paymentCollectionType);
    }
  }, [isEditGroup, formData.paymentCollectionType]);

  return (
    <>
      <div className={`w-full md:w-1/2 lg:w-1/3 ${groupDateClassName}`}>
        <GroupDueDateSelection
          formData={formData}
          isDueDateDisabled={isDueDateDisabled}
          isEditGroup={isEditGroup}
          isPaymentCollectionModalOpen={isPaymentCollectionModalOpen}
          setIsPaymentCollectionModalOpen={setIsPaymentCollectionModalOpen}
          dueDateValue={dueDateValue}
          onDueDateChange={onDueDateChange}
          paymentCollectionComponentType={paymentCollectionComponentType}
          byTermAdvancedDefaultLabel={byTermAdvancedDefaultLabel}
        />
      </div>

      <div className="flex flex-wrap items-start gap-2 md:flex-nowrap lg:gap-10">
        <GroupStartDateSelection
          formData={formData}
          isStartDateModalOpen={isStartDateModalOpen}
          onStartDateChange={onStartDateChange}
          setIsStartDateModalOpen={setIsStartDateModalOpen}
          isStartDateDisabled={isStartDateDisabled}
          groupStartDateClassName={groupStartDateClassName}
          startDateAlertClassName={startDateAlertClassName}
        />
        <GroupEndDateSelection
          groupEndDateClassName={groupEndDateClassName}
          formData={formData}
          error={error}
          onClearEndDate={onClearEndDate}
          setIsEndDateModalOpen={setIsEndDateModalOpen}
          isEndDateModalOpen={isEndDateModalOpen}
          onEndDateChange={onEndDateChange}
          endDateMinDate={endDateMinDate}
          endDateSelectedDate={endDateSelectedDate}
          isEndDateDisabled={isEndDateDisabled}
        />
      </div>
      {showConsent && !onPaymentLinkSentChange && (
        <GroupDueDateConsent
          setShowConsent={setShowConsent}
          collectionType={paymentCollectionComponentType}
          handleConsentConfirm={(data) => {
            setShowConsent(false);
            onInputChange('dueDateConsent', data);
          }}
        />
      )}
    </>
  );
};

export default GroupDateSettings;
